import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as warehouseService from '../../services/warehouseService';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  useTheme,
  IconButton,
  Collapse
} from '@mui/material';

// Material UI icons
import {
  FilterList as FilterListIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  AttachMoney as AttachMoneyIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';

const ReorderReport = () => {
  const theme = useTheme();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [filters, setFilters] = useState({
    warehouse_id: '',
    below_reorder_only: true
  });

  // Fetch warehouses on component mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const warehousesData = await warehouseService.getWarehouses();
        setWarehouses(warehousesData);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        // Don't set error state here to avoid blocking the report
      }
    };

    fetchWarehouses();
  }, []);

  // Fetch report data when filters change
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query string from filters
        let queryString = '';
        if (filters.warehouse_id) queryString += `warehouse_id=${filters.warehouse_id}&`;
        if (filters.below_reorder_only) queryString += `below_reorder_only=true&`;

        // Fetch report data
        const response = await axios.get(`/api/reports/reorder?${queryString}`);
        console.log('Reorder report data:', response.data);

        // Check if any parts have supplier_parts
        if (response.data && response.data.report) {
          const partsWithSuppliers = response.data.report.filter(
            item => item.part.supplier_parts && item.part.supplier_parts.length > 0
          );
          console.log('Parts with supplier_parts:', partsWithSuppliers.length);
          if (partsWithSuppliers.length > 0) {
            console.log('Example supplier_parts:', partsWithSuppliers[0].part.supplier_parts);
          }
        }

        setReport(response.data);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching reorder report:', err);
        setError('Failed to load reorder report');
        setLoading(false);
      }
    };

    fetchReport();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters({
      ...filters,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleRowExpand = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const handleExportCSV = () => {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to export data');
        return;
      }

      // Build query string from filters
      let queryString = '';
      if (filters.warehouse_id) queryString += `warehouse_id=${filters.warehouse_id}&`;
      if (filters.below_reorder_only) queryString += `below_reorder_only=true&`;

      // Remove trailing & if present
      if (queryString.endsWith('&')) {
        queryString = queryString.slice(0, -1);
      }

      // Use axios to make a request with the token in the header
      axios({
        url: `/api/reports/export/reorder?${queryString}`,
        method: 'GET',
        responseType: 'blob', // Important for handling file downloads
        headers: {
          'x-auth-token': token
        }
      }).then((response) => {
        // Create a blob URL for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'reorder_report.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch((error) => {
        console.error('Error downloading file:', error);
        alert('Failed to export report');
      });
    } catch (err) {
      console.error('Error exporting report:', err);
      alert('Failed to export report');
    }
  };

  const handleGeneratePurchaseOrder = () => {
    try {
      // Warehouse ID is required for purchase orders
      if (!filters.warehouse_id) {
        toast.error('Please select a warehouse to generate a purchase order');
        return;
      }

      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('You must be logged in to generate a purchase order');
        return;
      }

      // Build query string from filters
      let queryString = `warehouse_id=${filters.warehouse_id}`;
      if (filters.below_reorder_only) queryString += `&below_reorder_only=true`;

      // Use axios to make a request with the token in the header
      axios({
        url: `/api/reports/export/purchase-order?${queryString}`,
        method: 'GET',
        responseType: 'blob', // Important for handling file downloads
        headers: {
          'x-auth-token': token
        }
      }).then((response) => {
        // Create a blob URL for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // Get warehouse name for the filename
        const currentDate = new Date().toISOString().split('T')[0];
        let warehouseName = 'Warehouse';

        // Find the selected warehouse name from the warehouses array
        if (filters.warehouse_id && warehouses.length > 0) {
          const selectedWarehouse = warehouses.find(w => w.id.toString() === filters.warehouse_id);
          if (selectedWarehouse) {
            warehouseName = selectedWarehouse.name;
          }
        }

        link.setAttribute('download', `purchase_order_${warehouseName.replace(/\s+/g, '_')}_${currentDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('Purchase order generated successfully');
      }).catch((error) => {
        console.error('Error generating purchase order:', error);
        if (error.response && error.response.data && error.response.data.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error('Failed to generate purchase order');
        }
      });
    } catch (err) {
      console.error('Error generating purchase order:', err);
      toast.error('Failed to generate purchase order');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Reorder Points Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor inventory levels and identify items that need to be reordered
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ShoppingCartIcon />}
            onClick={handleGeneratePurchaseOrder}
            disabled={!filters.warehouse_id}
            title={!filters.warehouse_id ? "Select a warehouse to generate a purchase order" : ""}
          >
            Generate Purchase Order
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCSV}
          >
            Export to CSV
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          p: 2,
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6">
            Filters
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="warehouse-filter-label">Warehouse</InputLabel>
                <Select
                  labelId="warehouse-filter-label"
                  id="warehouse-filter"
                  name="warehouse_id"
                  value={filters.warehouse_id}
                  onChange={handleFilterChange}
                  label="Warehouse"
                >
                  <MenuItem value="">All Warehouses</MenuItem>
                  {warehouses.map(warehouse => (
                    <MenuItem key={warehouse.id} value={warehouse.id.toString()}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.below_reorder_only}
                    onChange={handleFilterChange}
                    name="below_reorder_only"
                    color="warning"
                  />
                }
                label="Show only items below reorder point"
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    bgcolor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <InventoryIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Parts
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {report.total_parts}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    bgcolor: 'error.main',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <WarningIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Parts Below Reorder Point
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {report.parts_below_reorder}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Parts Details Table */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{
              p: 2,
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <InventoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">
                Parts Details
              </Typography>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              <TableContainer>
                <Table size="medium" sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width="40px"></TableCell>
                      <TableCell width="120px">Part Number</TableCell>
                      <TableCell width="200px">Description</TableCell>
                      {filters.warehouse_id ? (
                        <TableCell align="right" width="120px">Warehouse Reorder Point</TableCell>
                      ) : (
                        <TableCell align="right" width="120px">Reorder Point</TableCell>
                      )}
                      <TableCell align="right" width="100px">Current Quantity</TableCell>
                      <TableCell width="150px">Status</TableCell>
                      <TableCell width="150px">Warehouse</TableCell>
                      <TableCell width="150px">Preferred Supplier</TableCell>
                      <TableCell width="150px">Least Expensive Supplier</TableCell>
                      <TableCell align="right" width="100px">Best Price</TableCell>
                      <TableCell align="center" width="200px">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.report.map(item => {
                      const rowId = `${item.part.id}-${item.warehouse?.id || 'global'}`;
                      const isExpanded = !!expandedRows[rowId];
                      const hasSupplierParts = item.part.supplier_parts && item.part.supplier_parts.length > 0;

                      return (
                        <React.Fragment key={rowId}>
                          <TableRow
                            hover
                            sx={{
                              ...(item.below_reorder_point && {
                                bgcolor: 'error.lighter',
                                '&:hover': {
                                  bgcolor: 'error.100',
                                }
                              })
                            }}
                          >
                            <TableCell>
                              {hasSupplierParts && (
                                <IconButton
                                  aria-label="expand row"
                                  size="small"
                                  onClick={() => handleRowExpand(rowId)}
                                >
                                  {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              <RouterLink
                                to={`/inventory/${item.part.id}`}
                                style={{
                                  color: theme.palette.primary.main,
                                  textDecoration: 'none',
                                  fontWeight: 500
                                }}
                              >
                                {item.part.internal_part_number}
                              </RouterLink>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={item.part.description}>
                                <Typography noWrap sx={{ maxWidth: 200 }}>
                                  {item.part.description}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              {item.part.warehouse_reorder_point !== null
                                ? item.part.warehouse_reorder_point
                                : item.part.global_reorder_point}
                              {item.part.warehouse_reorder_point !== null && (
                                <Tooltip title="Warehouse-specific reorder point">
                                  <Chip
                                    label="W"
                                    color="primary"
                                    size="small"
                                    sx={{ ml: 1, height: 20, width: 20, minWidth: 20 }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                ...(item.below_reorder_point && {
                                  color: 'error.main',
                                  fontWeight: 'bold'
                                })
                              }}
                            >
                              {item.total_quantity}
                            </TableCell>
                            <TableCell>
                              {item.below_reorder_point ? (
                                <Chip
                                  icon={<WarningIcon />}
                                  label="Below Reorder Point"
                                  color="error"
                                  size="small"
                                />
                              ) : (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="OK"
                                  color="success"
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {item.warehouse ? item.warehouse.name : 'All Warehouses'}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={item.part.preferred_supplier?.name || 'None'}>
                                <Typography noWrap sx={{ maxWidth: 150 }}>
                                  {item.part.preferred_supplier?.name || 'None'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={item.part.least_expensive_supplier?.name || 'None'}>
                                <Typography noWrap sx={{ maxWidth: 150 }}>
                                  {item.part.least_expensive_supplier?.name || 'None'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              {item.part.least_expensive_price ? `$${parseFloat(item.part.least_expensive_price).toFixed(2)}` : 'N/A'}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                <Tooltip title="View Details">
                                  <Button
                                    component={RouterLink}
                                    to={`/inventory/${item.part.id}`}
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                  >
                                    View
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Adjust Inventory">
                                  <Button
                                    component={RouterLink}
                                    to="/inventory/adjust"
                                    state={{ part_id: item.part.id }}
                                    variant="outlined"
                                    color="success"
                                    size="small"
                                    startIcon={<EditIcon />}
                                  >
                                    Adjust
                                  </Button>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Expandable row for supplier prices */}
                          {hasSupplierParts && (
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ margin: 2, backgroundColor: 'background.paper', borderRadius: 1, p: 2 }}>
                                    <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                                      <AttachMoneyIcon sx={{ mr: 1 }} />
                                      Supplier Prices
                                    </Typography>
                                    <Table size="small" aria-label="supplier prices">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Supplier</TableCell>
                                          <TableCell>Supplier Part Number</TableCell>
                                          <TableCell align="right">Price</TableCell>
                                          <TableCell align="right">Lead Time (Days)</TableCell>
                                          <TableCell>URL</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {item.part.supplier_parts.sort((a, b) => parseFloat(a.cost) - parseFloat(b.cost)).map((supplierPart) => (
                                          <TableRow key={supplierPart.id}>
                                            <TableCell component="th" scope="row">
                                              {supplierPart.supplier.name}
                                            </TableCell>
                                            <TableCell>{supplierPart.supplier_part_number}</TableCell>
                                            <TableCell align="right">${parseFloat(supplierPart.cost).toFixed(2)}</TableCell>
                                            <TableCell align="right">{supplierPart.lead_time_days || 'N/A'}</TableCell>
                                            <TableCell>
                                              {supplierPart.url ? (
                                                <Button
                                                  href={supplierPart.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  size="small"
                                                  variant="text"
                                                >
                                                  View
                                                </Button>
                                              ) : (
                                                'N/A'
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          No reorder data available.
        </Alert>
      )}
    </Container>
  );
};

export default ReorderReport;
